/**
 * The controls every widget settings form is built from, bound to one item's
 * contentProps.
 *
 * Text fields keep a local draft and write through the editor's pending-edits
 * queue (debounced, so the canvas follows the typing without one write per
 * keystroke; flushed before undo, on close and on selection change). Discrete
 * controls — a select, a segmented choice, a switch — write at once, after
 * flushing any pending text so writes land in the order they were made.
 */
import { useEffect, useState } from "react";
import {
  Field,
  Icon,
  Input,
  Inline,
  Select,
  Switch,
  Textarea,
  Toggle,
  ToggleGroup,
  type IconName,
} from "@duro-app/ui";
import { useGridStore, type GridItem } from "@fredericrous/grid";
import { InputIcon } from "@website-builder/schemas";
import {
  flushPendingEdits,
  hasPendingEdit,
  patchContentProps,
  scheduleEdit,
} from "@website-builder/editor";

export interface ItemRef {
  readonly gridId: string;
  readonly itemId: string;
}

/** The item, live from the grid store. */
export function useItem({ gridId, itemId }: ItemRef): GridItem | undefined {
  return useGridStore((s) => s.grids[gridId]?.items[itemId]);
}

/** The item's contentProps, never undefined. */
export function useProps(ref: ItemRef): Record<string, unknown> {
  return (useItem(ref)?.contentProps ?? {}) as Record<string, unknown>;
}

/** Write a discrete change now (after any pending text). */
export function setNow(
  { gridId, itemId }: ItemRef,
  patch: Record<string, unknown>,
): void {
  flushPendingEdits();
  patchContentProps(gridId, itemId, patch);
}

/** Whether the item sits in a section that is a form (ADR-0009: the section
 *  owns the form; a submit button only means something inside one). */
export function useInFormSection({ gridId }: ItemRef): boolean {
  return useGridStore((s) => {
    const grid = s.grids[gridId];
    if (!grid?.parentGridId || !grid.parentItemId) return false;
    const section = s.grids[grid.parentGridId]?.items[grid.parentItemId];
    const cp = section?.contentProps as Record<string, unknown> | undefined;
    return cp?.["form"] !== undefined;
  });
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** A text prop's draft: follows the document whenever nothing is pending
 *  (so undo/redo and other clients show up), writes debounced. */
function useTextDraft(
  ref: ItemRef,
  key: string,
): [string, (next: string) => void] {
  const stored = str(useProps(ref)[key]);
  const [draft, setDraft] = useState(stored);
  useEffect(() => {
    if (!hasPendingEdit(ref.itemId)) setDraft(stored);
  }, [stored, ref.itemId]);
  const change = (next: string): void => {
    setDraft(next);
    scheduleEdit(ref.gridId, ref.itemId, { [key]: next });
  };
  return [draft, change];
}

export function TextSetting({
  item,
  prop,
  label,
  description,
  placeholder,
  type,
  disabled,
  invalid,
  error,
}: {
  readonly item: ItemRef;
  readonly prop: string;
  readonly label: string;
  readonly description?: string;
  readonly placeholder?: string;
  readonly type?: "text" | "url";
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly error?: string;
}): React.ReactElement {
  const [value, change] = useTextDraft(item, prop);
  return (
    <Field.Root disabled={disabled} invalid={invalid}>
      <Field.Label>{label}</Field.Label>
      <Input
        type={type ?? "text"}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => change(e.target.value)}
        onBlur={flushPendingEdits}
      />
      {description && <Field.Description>{description}</Field.Description>}
      {invalid && error && <Field.Error>{error}</Field.Error>}
    </Field.Root>
  );
}

export function TextAreaSetting({
  item,
  prop,
  label,
}: {
  readonly item: ItemRef;
  readonly prop: string;
  readonly label: string;
}): React.ReactElement {
  const [value, change] = useTextDraft(item, prop);
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <Textarea
        value={value}
        rows={3}
        onChange={(e) => change(e.target.value)}
        onBlur={flushPendingEdits}
      />
    </Field.Root>
  );
}

export interface Choice<T extends string> {
  readonly value: T;
  readonly label: string;
}

export function SelectSetting<T extends string>({
  item,
  prop,
  label,
  choices,
  fallback,
  toProp,
}: {
  readonly item: ItemRef;
  readonly prop: string;
  readonly label: string;
  readonly choices: readonly Choice<T>[];
  readonly fallback: T;
  /** Maps the chosen value to what is stored (e.g. "none" → undefined). */
  readonly toProp?: (v: T) => unknown;
}): React.ReactElement {
  const raw = useProps(item)[prop];
  const value =
    choices.find((c) => String(c.value) === String(raw ?? ""))?.value ??
    fallback;
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <Select.Root
        value={value}
        onValueChange={(v) => {
          const next = choices.find((c) => c.value === v);
          if (next)
            setNow(item, {
              [prop]: toProp ? toProp(next.value) : next.value,
            });
        }}
      >
        <Select.Trigger aria-label={label}>
          <Select.Value />
          <Select.Icon />
        </Select.Trigger>
        <Select.Popup>
          {choices.map((c) => (
            <Select.Item key={c.value} value={c.value}>
              {c.label}
            </Select.Item>
          ))}
        </Select.Popup>
      </Select.Root>
    </Field.Root>
  );
}

export function SegmentedSetting<T extends string>({
  item,
  prop,
  label,
  choices,
  fallback,
  toProp,
}: {
  readonly item: ItemRef;
  readonly prop: string;
  readonly label: string;
  readonly choices: readonly Choice<T>[];
  readonly fallback: T;
  /** Maps the chosen value to what is stored (e.g. "3" → 3). */
  readonly toProp?: (v: T) => unknown;
}): React.ReactElement {
  const raw = useProps(item)[prop];
  const value =
    choices.find((c) => String(c.value) === String(raw ?? ""))?.value ??
    fallback;
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      {/* Inline so the group hugs its options instead of stretching. */}
      <Inline>
        <ToggleGroup
          size="small"
          value={[value]}
          onValueChange={(v) => {
            // Pressing the active option empties a single group; a prop
            // always has a value, so ignore that.
            const next = choices.find((c) => c.value === v[0]);
            if (next)
              setNow(item, {
                [prop]: toProp ? toProp(next.value) : next.value,
              });
          }}
        >
          {choices.map((c) => (
            <Toggle key={c.value} value={c.value}>
              {c.label}
            </Toggle>
          ))}
        </ToggleGroup>
      </Inline>
    </Field.Root>
  );
}

/** A choice shown as an icon; one without `icon` shows its label as text. */
export interface IconChoice {
  readonly value: string;
  readonly label: string;
  readonly icon?: IconName;
}

/** The icons an input group or an Icon widget can show, defined once. */
export const ICON_OPTIONS: readonly IconChoice[] = InputIcon.literals.map(
  (v) => ({
    value: v,
    label: v.charAt(0).toUpperCase() + v.slice(1),
    icon: v,
  }),
);

/** The icon list for a value held outside contentProps (an inline row's
 *  item); IconSetting binds it to a prop. */
export function IconList({
  label,
  choices,
  value,
  onChange,
}: {
  readonly label: string;
  readonly choices: readonly IconChoice[];
  readonly value: string;
  readonly onChange: (v: string) => void;
}): React.ReactElement {
  const selected = choices.find((c) => c.value === value);
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <ToggleGroup
        wrap
        maxRows={3}
        size="small"
        value={selected ? [selected.value] : []}
        onValueChange={(v) => {
          // Pressing the active icon empties a single group; a value is
          // always held, so ignore that.
          const next = choices.find((c) => c.value === v[0]);
          if (next) onChange(next.value);
        }}
      >
        {choices.map((c) => (
          <Toggle key={c.value} value={c.value} aria-label={c.label}>
            {c.icon ? <Icon name={c.icon} size="md" /> : c.label}
          </Toggle>
        ))}
      </ToggleGroup>
      <Field.Description>{selected?.label}</Field.Description>
    </Field.Root>
  );
}

export function IconSetting({
  item,
  prop,
  label,
  choices,
  fallback,
  toProp,
}: {
  readonly item: ItemRef;
  readonly prop: string;
  readonly label: string;
  readonly choices: readonly IconChoice[];
  readonly fallback: string;
  /** Maps the chosen value to what is stored (e.g. "none" → undefined). */
  readonly toProp?: (v: string) => unknown;
}): React.ReactElement {
  const raw = useProps(item)[prop];
  const value = choices.find((c) => c.value === raw)?.value ?? fallback;
  return (
    <IconList
      label={label}
      choices={choices}
      value={value}
      onChange={(v) => setNow(item, { [prop]: toProp ? toProp(v) : v })}
    />
  );
}

export function SwitchSetting({
  item,
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  readonly item: ItemRef;
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => Record<string, unknown>;
  readonly disabled?: boolean;
  readonly description?: string;
}): React.ReactElement {
  return (
    <Field.Root disabled={disabled}>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(c) => setNow(item, onChange(c))}
      >
        {label}
      </Switch>
      {description && <Field.Description>{description}</Field.Description>}
    </Field.Root>
  );
}
